import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useApiClient } from './useApiClient';
import type { components } from './types.gen';

type Schemas = components['schemas'];

export type Workspace = Schemas['Workspace'];
export type Membership = Schemas['Membership'];
export type Project = Schemas['Project'];
export type Language = Schemas['Language'];
export type LanguageAccess = Schemas['LanguageAccess'];
export type ProjectPermission = Schemas['ProjectPermission'];
export type ProjectToken = Schemas['ProjectToken'];
export type ImportSummary = Schemas['ImportSummary'];
export type ImportJob = Schemas['ImportJob'];
export type TranslateJob = Schemas['TranslateJob'];
export type TranslateJobStatus = TranslateJob['status'];
export type SelfInfo = Schemas['SelfInfo'];
export type Integration = Schemas['Integration'];
export type AutoTranslate = Schemas['AutoTranslate'];
export type WorkspaceCredential = Schemas['WorkspaceCredential'];
export type CredentialSecret = Schemas['CredentialSecret'];
export type CredentialKind = WorkspaceCredential['kind'];
export type AIConfig = Schemas['AIConfig'];
export type WorkspaceToken = Schemas['WorkspaceToken'];
export type IssuedWorkspaceToken = Schemas['IssuedWorkspaceToken'];
export type WorkspaceTokenScope = Schemas['WorkspaceTokenScope'];
export type WorkspaceRole = Membership['role'];
export type PublishTarget = Schemas['PublishTarget'];
export type CreatePublishTargetRequest = Schemas['CreatePublishTargetRequest'];
export type PublishJob = Schemas['PublishJob'];
export type PublishSummary = Schemas['PublishSummary'];
export type PublishTrigger = NonNullable<PublishTarget['triggers']>[number];
export type PublishJobStatus = PublishJob['status'];
export type Key = Schemas['Key'];
export type Translation = Schemas['Translation'];
export type TranslationRevision = Schemas['TranslationRevision'];
export type TranslationStatus = Translation['status'];

export interface GlossaryTerm {
  source?: string;
  caseSensitive?: boolean;
  doNotTranslate?: boolean;
  description?: string;
  translations?: Record<string, string>;
}

export interface Glossary {
  id?: string;
  workspaceId?: string;
  projectId?: string;
  styleGuide?: string;
  terms?: GlossaryTerm[];
  updatedAt?: string;
}

export interface TranslateRunResult {
  provider: string;
  model: string;
  totalTokens: number;
  totalCostUSD: number;
  durationMs: number;
  languages: {
    language: string;
    keysConsidered: number;
    translated: number;
    fuzzy?: number;
    tmHits?: number;
    skipped?: number;
    errors?: number;
    apiCalls?: number;
    inputTokens?: number;
    outputTokens?: number;
    costUSD?: number;
    durationMs?: number;
  }[];
  fuzzy?: {
    keyId?: string;
    language?: string;
    source?: string;
    value?: string;
    missing?: string[];
    extra?: string[];
  }[];
}

export const qk = {
  self: () => ['self'] as const,
  workspaces: () => ['workspaces'] as const,
  workspace: (id: string) => ['workspace', id] as const,
  workspaceMembers: (id: string) => ['workspace', id, 'members'] as const,
  workspaceLanguages: (id: string) => ['workspace', id, 'languages'] as const,
  workspaceProjects: (id: string) => ['workspace', id, 'projects'] as const,
  project: (id: string) => ['project', id] as const,
  projectPermissions: (id: string) => ['project', id, 'permissions'] as const,
  projectKeys: (id: string) => ['project', id, 'keys'] as const,
  projectTranslations: (id: string, lang: string) =>
    ['project', id, 'translations', lang] as const,
  keyTranslations: (keyID: string) => ['key', keyID, 'translations'] as const,
  translationRevisions: (keyID: string, lang: string) =>
    ['key', keyID, 'translations', lang, 'revisions'] as const,
  projectGlossary: (id: string) => ['project', id, 'glossary'] as const,
  workspaceCredentials: (id: string) => ['workspace', id, 'credentials'] as const,
  workspaceAIConfig: (id: string) => ['workspace', id, 'ai-config'] as const,
  workspaceTokens: (id: string) => ['workspace', id, 'api-tokens'] as const,
  publishTargets: (id: string) => ['project', id, 'publish-targets'] as const,
  publishJobs: (id: string) => ['project', id, 'publish-jobs'] as const,
};

function unwrap<T>(data: T | undefined, error: unknown): T {
  if (error) {
    throw normaliseError(error);
  }
  if (data === undefined) {
    throw new Error('Empty response body');
  }
  return data;
}

interface ApiErrorShape {
  code?: string;
  message?: string;
}

export class ApiError extends Error {
  code?: string;
  status?: number;
  constructor(message: string, opts: { code?: string; status?: number } = {}) {
    super(message);
    this.name = 'ApiError';
    this.code = opts.code;
    this.status = opts.status;
  }
}

function normaliseError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (typeof err === 'object' && err !== null) {
    const shape = err as ApiErrorShape & { status?: number };
    return new ApiError(shape.message ?? 'Request failed', {
      code: shape.code,
      status: shape.status,
    });
  }
  return new Error('Request failed');
}

export function useSelf() {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.self(),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/self');
      return unwrap(data, error);
    },
  });
}

export function useWorkspaces() {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.workspaces(),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/workspaces');
      const body = unwrap(data, error);
      return body.items ?? [];
    },
  });
}

export function useWorkspace(id: string | undefined, opts?: Partial<UseQueryOptions<Workspace>>) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.workspace(id ?? ''),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/workspaces/{workspaceID}', {
        params: { path: { workspaceID: id! } },
      });
      return unwrap(data, error);
    },
    ...opts,
  });
}

interface CreateWorkspaceInput {
  name: string;
  slug: string;
  plan?: string;
}

export function useCreateWorkspace(
  options?: UseMutationOptions<Workspace, Error, CreateWorkspaceInput>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const { data, error } = await client.POST('/v1/workspaces', { body: input });
      return unwrap(data, error);
    },
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: qk.workspaces() });
      options?.onSuccess?.(...args);
    },
    ...options,
  });
}

export function useWorkspaceMembers(workspaceID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.workspaceMembers(workspaceID ?? ''),
    enabled: Boolean(workspaceID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/workspaces/{workspaceID}/members', {
        params: { path: { workspaceID: workspaceID! } },
      });
      const body = unwrap(data, error);
      return body.items ?? [];
    },
  });
}

interface UpsertMemberInput {
  workspaceID: string;
  userSubject: string;
  email?: string;
  displayName?: string;
  role: 'owner' | 'admin' | 'translator' | 'viewer';
}

export function useUpsertMember() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceID, ...body }: UpsertMemberInput) => {
      const { data, error } = await client.POST('/v1/workspaces/{workspaceID}/members', {
        params: { path: { workspaceID } },
        body,
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.workspaceMembers(vars.workspaceID) });
    },
  });
}

export function useRemoveMember() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      workspaceID,
      userSubject,
    }: {
      workspaceID: string;
      userSubject: string;
    }) => {
      const { error } = await client.DELETE(
        '/v1/workspaces/{workspaceID}/members/{userSubject}',
        { params: { path: { workspaceID, userSubject } } },
      );
      if (error) throw normaliseError(error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.workspaceMembers(vars.workspaceID) });
    },
  });
}

export function useWorkspaceLanguages(workspaceID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.workspaceLanguages(workspaceID ?? ''),
    enabled: Boolean(workspaceID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/workspaces/{workspaceID}/languages', {
        params: { path: { workspaceID: workspaceID! } },
      });
      const body = unwrap(data, error);
      return body.items ?? [];
    },
  });
}

interface UpsertLanguageInput {
  workspaceID: string;
  code: string;
  label?: string;
  pluralForms?: string;
}

export function useUpsertLanguage() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceID, ...body }: UpsertLanguageInput) => {
      const { data, error } = await client.POST('/v1/workspaces/{workspaceID}/languages', {
        params: { path: { workspaceID } },
        body,
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.workspaceLanguages(vars.workspaceID) });
    },
  });
}

export function useWorkspaceProjects(workspaceID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.workspaceProjects(workspaceID ?? ''),
    enabled: Boolean(workspaceID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/workspaces/{workspaceID}/projects', {
        params: { path: { workspaceID: workspaceID! } },
      });
      const body = unwrap(data, error);
      return body.items ?? [];
    },
  });
}

interface CreateProjectInput {
  workspaceID: string;
  name: string;
  slug: string;
  sourceLanguageCode: string;
  targetLanguageCodes?: string[];
  defaultFormat?: 'po' | 'pot' | 'json';
  integration?: Integration;
}

export function useCreateProject() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceID, ...body }: CreateProjectInput) => {
      const { data, error } = await client.POST('/v1/workspaces/{workspaceID}/projects', {
        params: { path: { workspaceID } },
        body,
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.workspaceProjects(vars.workspaceID) });
    },
  });
}

export function useProject(id: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.project(id ?? ''),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/projects/{projectID}', {
        params: { path: { projectID: id! } },
      });
      return unwrap(data, error);
    },
  });
}

interface UpdateProjectInput {
  projectID: string;
  name?: string;
  sourceLanguageCode?: string;
  targetLanguageCodes?: string[];
  defaultFormat?: string;
  integration?: Integration;
  autoTranslate?: AutoTranslate;
}

export function useUpdateProject() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectID, ...body }: UpdateProjectInput) => {
      const { data, error } = await client.PATCH('/v1/projects/{projectID}', {
        params: { path: { projectID } },
        body,
      });
      return unwrap(data, error);
    },
    onSuccess: (proj, vars) => {
      void qc.invalidateQueries({ queryKey: qk.project(vars.projectID) });
      if (proj.workspaceId) {
        void qc.invalidateQueries({ queryKey: qk.workspaceProjects(proj.workspaceId) });
      }
    },
  });
}

export function useRotateProjectToken() {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (projectID: string) => {
      const { data, error } = await client.POST('/v1/projects/{projectID}/tokens/rotate', {
        params: { path: { projectID } },
      });
      return unwrap(data, error);
    },
  });
}

export function useProjectPermissions(projectID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.projectPermissions(projectID ?? ''),
    enabled: Boolean(projectID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/projects/{projectID}/permissions', {
        params: { path: { projectID: projectID! } },
      });
      const body = unwrap(data, error);
      return body.items ?? [];
    },
  });
}

interface GrantPermissionInput {
  projectID: string;
  userSubject: string;
  languages: LanguageAccess[];
}

export function useGrantPermission() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectID, userSubject, languages }: GrantPermissionInput) => {
      const { data, error } = await client.PUT(
        '/v1/projects/{projectID}/permissions/{userSubject}',
        {
          params: { path: { projectID, userSubject } },
          body: { languages },
        },
      );
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.projectPermissions(vars.projectID) });
    },
  });
}

export function useRevokePermission() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectID,
      userSubject,
    }: {
      projectID: string;
      userSubject: string;
    }) => {
      const { error } = await client.DELETE(
        '/v1/projects/{projectID}/permissions/{userSubject}',
        { params: { path: { projectID, userSubject } } },
      );
      if (error) throw normaliseError(error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.projectPermissions(vars.projectID) });
    },
  });
}

export interface ImportFileInput {
  projectID: string;
  file: File;
  lang?: string;
  format?: 'po' | 'pot' | 'json';
  filename?: string;
  namespace?: string;
}

export function useImportFile() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    // The upload endpoint is asynchronous: it returns 202 + an ImportJob and a
    // background worker does the parsing/segment writing. We enqueue, then poll
    // the job to a terminal state so the caller still gets an ImportSummary (or
    // an error) when processing finishes — without the upload request itself
    // blocking long enough to trip the gateway 502 it used to.
    mutationFn: async (input: ImportFileInput): Promise<ImportSummary> => {
      // openapi-fetch wants a `string` body for octet-stream (per the OpenAPI
      // spec), but the wire format is binary. We stream the File through the
      // serializer untouched so fetch sends the raw bytes.
      const { data, error } = await client.POST('/v1/projects/{projectID}/files/import', {
        params: {
          path: { projectID: input.projectID },
          query: {
            lang: input.lang,
            format: input.format,
            filename: input.filename ?? input.file.name,
            namespace: input.namespace,
          },
        },
        body: input.file as unknown as string,
        bodySerializer: (body) => body,
        headers: { 'Content-Type': 'application/octet-stream' },
      });
      const job = unwrap(data, error) as ImportJob;
      return await pollImportJob(client, input.projectID, job.id);
    },
    onSuccess: (_summary, vars) => {
      void qc.invalidateQueries({ queryKey: qk.project(vars.projectID) });
    },
  });
}

/**
 * Poll an import job until it succeeds or fails. Resolves with the summary on
 * success; rejects with an {@link ApiError} carrying the recorded error on
 * failure. Each poll is a fast request, so a slow import never blocks a single
 * long-lived HTTP request.
 */
async function pollImportJob(
  client: ReturnType<typeof useApiClient>,
  projectID: string,
  jobID: string,
): Promise<ImportSummary> {
  const intervalMs = 750;
  const deadline = Date.now() + 10 * 60 * 1000; // 10 min ceiling
  for (;;) {
    const { data, error } = await client.GET('/v1/projects/{projectID}/imports/{jobID}', {
      params: { path: { projectID, jobID } },
    });
    const job = unwrap(data, error) as ImportJob;
    if (job.status === 'succeeded') {
      return (job.summary ?? {}) as ImportSummary;
    }
    if (job.status === 'failed') {
      throw new ApiError(job.error || 'Import failed', { code: 'import_failed' });
    }
    if (Date.now() > deadline) {
      throw new ApiError('Import timed out while processing', { code: 'import_timeout' });
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

/**
 * Build the absolute URL for an export download. We hand this to a temporary
 * anchor so the browser handles the file download without buffering in JS,
 * but the OIDC bearer token is still required — so use {@link useExportFile}
 * for the auth-aware path.
 */
export function buildExportUrl(projectID: string, lang: string, format?: 'po' | 'pot' | 'json') {
  const u = new URL(`/v1/projects/${projectID}/files/export`, window.location.origin);
  u.searchParams.set('lang', lang);
  if (format) u.searchParams.set('format', format);
  return u.toString();
}

interface ExportFileInput {
  projectID: string;
  lang: string;
  format?: 'po' | 'pot' | 'json';
  suggestedName?: string;
}

export function useProjectKeys(projectID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.projectKeys(projectID ?? ''),
    enabled: Boolean(projectID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/projects/{projectID}/keys', {
        params: { path: { projectID: projectID! }, query: { limit: 5000 } },
      });
      const body = unwrap(data, error);
      return (body.items ?? []) as Key[];
    },
  });
}

export function useProjectTranslations(projectID: string | undefined, lang: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.projectTranslations(projectID ?? '', lang ?? ''),
    enabled: Boolean(projectID) && Boolean(lang),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/projects/{projectID}/translations', {
        params: { path: { projectID: projectID! }, query: { lang: lang! } },
      });
      const body = unwrap(data, error);
      return (body.items ?? []) as Translation[];
    },
  });
}

export function useKeyTranslations(keyID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.keyTranslations(keyID ?? ''),
    enabled: Boolean(keyID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/keys/{keyID}/translations', {
        params: { path: { keyID: keyID! } },
      });
      const body = unwrap(data, error);
      return (body.items ?? []) as Translation[];
    },
  });
}

interface UpsertTranslationInput {
  keyID: string;
  lang: string;
  value: string;
  pluralValues?: string[];
  status?: TranslationStatus;
  source?: string;
  /** Project context — used to invalidate the project-wide translation query. */
  projectID?: string;
}

export function useUpsertTranslation() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ keyID, lang, projectID: _projectID, ...body }: UpsertTranslationInput) => {
      const { data, error } = await client.PUT('/v1/keys/{keyID}/translations/{lang}', {
        params: { path: { keyID, lang } },
        body,
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.keyTranslations(vars.keyID) });
      if (vars.projectID) {
        void qc.invalidateQueries({
          queryKey: qk.projectTranslations(vars.projectID, vars.lang),
        });
      }
      void qc.invalidateQueries({ queryKey: qk.translationRevisions(vars.keyID, vars.lang) });
    },
  });
}

export function useTranslationRevisions(keyID: string | undefined, lang: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.translationRevisions(keyID ?? '', lang ?? ''),
    enabled: Boolean(keyID) && Boolean(lang),
    queryFn: async () => {
      const { data, error } = await client.GET(
        '/v1/keys/{keyID}/translations/{lang}/revisions',
        { params: { path: { keyID: keyID!, lang: lang! } } },
      );
      const body = unwrap(data, error);
      return (body.items ?? []) as TranslationRevision[];
    },
  });
}

/** Live progress snapshot reported while an async translate job runs. */
export interface TranslateProgress {
  status: TranslateJobStatus;
  /** Candidate keys discovered across all target languages (0 until claimed). */
  total: number;
  translated: number;
  failed: number;
  remaining: number;
}

interface RunTranslateInput {
  projectID: string;
  languages?: string[];
  onlyMissing?: boolean;
  includeFuzzy?: boolean;
  batchSize?: number;
  model?: string;
  dryRun?: boolean;
  /** Invoked on every poll tick with the latest counters (for progress UI). */
  onProgress?: (p: TranslateProgress) => void;
}

/**
 * Poll a translate job until it succeeds or fails. The run is enqueued as a
 * background job (202 + job id) so a large project is no longer capped by the
 * request write timeout; each poll is a fast request and `onProgress` fires
 * every tick so the UI can show total / translated / failed / remaining. Mirrors
 * {@link pollImportJob}. Resolves with the terminal job (carrying `result` on
 * success); rejects with an {@link ApiError} carrying the recorded error.
 */
async function pollTranslateJob(
  client: ReturnType<typeof useApiClient>,
  projectID: string,
  jobID: string,
  onProgress?: (p: TranslateProgress) => void,
): Promise<TranslateJob> {
  const intervalMs = 1000;
  const deadline = Date.now() + 30 * 60 * 1000; // 30 min ceiling — large projects
  for (;;) {
    const { data, error } = await client.GET('/v1/projects/{projectID}/translate/jobs/{jobID}', {
      params: { path: { projectID, jobID } },
    });
    const job = unwrap(data, error) as TranslateJob;
    const total = job.totalKeys ?? 0;
    const translated = job.translated ?? 0;
    const failed = job.failed ?? 0;
    onProgress?.({
      status: job.status,
      total,
      translated,
      failed,
      remaining: Math.max(0, total - translated - failed),
    });
    if (job.status === 'succeeded') {
      return job;
    }
    if (job.status === 'failed') {
      throw new ApiError(job.error || 'Translation failed', { code: 'translate_failed' });
    }
    if (Date.now() > deadline) {
      throw new ApiError('Translation timed out while processing', { code: 'translate_timeout' });
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

export function useRunTranslate() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    // The translate endpoint is asynchronous: POST enqueues a job and returns
    // 202 + a TranslateJob; a background worker drives the run off the request
    // path. We enqueue, then poll the job to a terminal state (reporting live
    // progress) so the caller still gets the full TranslateRunResult when the
    // run finishes — without one long-lived request that the 30s timeout used
    // to cap at ~280 entries.
    mutationFn: async ({
      projectID,
      onProgress,
      ...body
    }: RunTranslateInput): Promise<TranslateRunResult> => {
      const { data, error } = await client.POST('/v1/projects/{projectID}/translate', {
        params: { path: { projectID } },
        body,
      });
      const job = unwrap(data, error) as TranslateJob;
      const done = await pollTranslateJob(client, projectID, job.id, onProgress);
      // A succeeded job always carries `result`; fall back defensively so the
      // single-key "Translate again" path never dereferences undefined.
      return (done.result ?? {
        provider: '',
        model: '',
        totalTokens: 0,
        totalCostUSD: 0,
        durationMs: 0,
        languages: [],
      }) as TranslateRunResult;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.projectKeys(vars.projectID) });
      void qc.invalidateQueries({
        predicate: (q) => {
          const key = q.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === 'project' &&
            key[1] === vars.projectID &&
            key[2] === 'translations'
          );
        },
      });
    },
  });
}

export function useProjectGlossary(projectID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.projectGlossary(projectID ?? ''),
    enabled: Boolean(projectID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/projects/{projectID}/glossary', {
        params: { path: { projectID: projectID! } },
      });
      return unwrap(data, error) as Glossary;
    },
  });
}

interface UpsertGlossaryInput {
  projectID: string;
  styleGuide?: string;
  terms?: GlossaryTerm[];
}

export function useUpsertGlossary() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectID, ...body }: UpsertGlossaryInput) => {
      const { error } = await client.PUT('/v1/projects/{projectID}/glossary', {
        params: { path: { projectID } },
        body,
      });
      if (error) throw normaliseError(error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.projectGlossary(vars.projectID) });
    },
  });
}

/**
 * Download an export with the current bearer token. Returns nothing — the
 * browser receives a synthetic anchor click that triggers a real download.
 */
export function useExportFile() {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (input: ExportFileInput) => {
      const { data, error, response } = await client.GET(
        '/v1/projects/{projectID}/files/export',
        {
          params: {
            path: { projectID: input.projectID },
            query: { lang: input.lang, format: input.format },
          },
          parseAs: 'blob',
        },
      );
      if (error) throw normaliseError(error);
      const blob = data instanceof Blob ? data : await response.blob();
      const filename =
        input.suggestedName ??
        `${input.projectID}-${input.lang}.${input.format ?? 'po'}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1_000);
      return filename;
    },
  });
}

/**
 * Resolve the current user's role within a workspace by matching the self
 * subject against the membership list. Returns `undefined` while either query
 * is still loading or when the user has no membership row. Used to gate
 * owner/admin-only surfaces in the UI (the API enforces the same server-side).
 */
export function useWorkspaceRole(workspaceID: string | undefined): {
  role: WorkspaceRole | undefined;
  isLoading: boolean;
  canManage: boolean;
} {
  const self = useSelf();
  const members = useWorkspaceMembers(workspaceID);
  const subject = self.data?.subject;
  const role = subject
    ? members.data?.find((m) => m.userSubject === subject)?.role
    : undefined;
  return {
    role,
    isLoading: self.isLoading || members.isLoading,
    canManage: role === 'owner' || role === 'admin',
  };
}

export function useWorkspaceCredentials(workspaceID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.workspaceCredentials(workspaceID ?? ''),
    enabled: Boolean(workspaceID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/workspaces/{workspaceID}/credentials', {
        params: { path: { workspaceID: workspaceID! } },
      });
      const body = unwrap(data, error);
      return body.items ?? [];
    },
  });
}

interface CreateCredentialInput {
  workspaceID: string;
  kind: CredentialKind;
  name: string;
  provider?: string;
  secret: CredentialSecret;
}

export function useCreateCredential() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceID, ...body }: CreateCredentialInput) => {
      const { data, error } = await client.POST('/v1/workspaces/{workspaceID}/credentials', {
        params: { path: { workspaceID } },
        body,
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.workspaceCredentials(vars.workspaceID) });
    },
  });
}

interface RotateCredentialInput {
  workspaceID: string;
  credentialID: string;
  secret: CredentialSecret;
}

export function useRotateCredential() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceID, credentialID, secret }: RotateCredentialInput) => {
      const { data, error } = await client.PUT(
        '/v1/workspaces/{workspaceID}/credentials/{credentialID}',
        { params: { path: { workspaceID, credentialID } }, body: { secret } },
      );
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.workspaceCredentials(vars.workspaceID) });
    },
  });
}

export function useDeleteCredential() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      workspaceID,
      credentialID,
    }: {
      workspaceID: string;
      credentialID: string;
    }) => {
      const { error } = await client.DELETE(
        '/v1/workspaces/{workspaceID}/credentials/{credentialID}',
        { params: { path: { workspaceID, credentialID } } },
      );
      if (error) throw normaliseError(error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.workspaceCredentials(vars.workspaceID) });
    },
  });
}

export function useAIConfig(workspaceID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.workspaceAIConfig(workspaceID ?? ''),
    enabled: Boolean(workspaceID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/workspaces/{workspaceID}/ai-config', {
        params: { path: { workspaceID: workspaceID! } },
      });
      return unwrap(data, error);
    },
  });
}

interface UpdateAIConfigInput extends AIConfig {
  workspaceID: string;
}

export function useUpdateAIConfig() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceID, ...body }: UpdateAIConfigInput) => {
      const { data, error } = await client.PUT('/v1/workspaces/{workspaceID}/ai-config', {
        params: { path: { workspaceID } },
        body,
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.workspaceAIConfig(vars.workspaceID) });
    },
  });
}

// --- COR-612 T5.2: project publish targets + runs -----------------------------

export function usePublishTargets(projectID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.publishTargets(projectID ?? ''),
    enabled: Boolean(projectID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/projects/{projectID}/publish-targets', {
        params: { path: { projectID: projectID! } },
      });
      const body = unwrap(data, error);
      return body.items ?? [];
    },
  });
}

interface CreatePublishTargetInput extends CreatePublishTargetRequest {
  projectID: string;
}

export function useCreatePublishTarget() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectID, ...body }: CreatePublishTargetInput) => {
      const { data, error } = await client.POST('/v1/projects/{projectID}/publish-targets', {
        params: { path: { projectID } },
        body,
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.publishTargets(vars.projectID) });
    },
  });
}

interface UpdatePublishTargetInput extends CreatePublishTargetRequest {
  projectID: string;
  targetID: string;
}

export function useUpdatePublishTarget() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectID, targetID, ...body }: UpdatePublishTargetInput) => {
      const { data, error } = await client.PUT(
        '/v1/projects/{projectID}/publish-targets/{targetID}',
        { params: { path: { projectID, targetID } }, body },
      );
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.publishTargets(vars.projectID) });
    },
  });
}

export function useDeletePublishTarget() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectID,
      targetID,
    }: {
      projectID: string;
      targetID: string;
    }) => {
      const { error } = await client.DELETE(
        '/v1/projects/{projectID}/publish-targets/{targetID}',
        { params: { path: { projectID, targetID } } },
      );
      if (error) throw normaliseError(error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.publishTargets(vars.projectID) });
    },
  });
}

/**
 * Enqueue a manual publish run. Returns the freshly-queued {@link PublishJob};
 * the caller watches {@link usePublishJobs} (which polls while a job is active)
 * to follow it to a terminal state.
 */
export function useRunPublishTarget() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectID,
      targetID,
    }: {
      projectID: string;
      targetID: string;
    }) => {
      const { data, error } = await client.POST(
        '/v1/projects/{projectID}/publish-targets/{targetID}/run',
        { params: { path: { projectID, targetID } } },
      );
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.publishJobs(vars.projectID) });
    },
  });
}

/**
 * List recent publish jobs (newest first). While any job is still queued or
 * running we poll every few seconds so the run-history view advances to the
 * terminal state on its own — mirroring the import-job poll, but list-driven
 * because the run history is always on screen.
 */
export function usePublishJobs(projectID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.publishJobs(projectID ?? ''),
    enabled: Boolean(projectID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/projects/{projectID}/publish-jobs', {
        params: { path: { projectID: projectID! } },
      });
      const body = unwrap(data, error);
      return body.items ?? [];
    },
    refetchInterval: (query) => {
      const jobs = query.state.data as PublishJob[] | undefined;
      const active = jobs?.some((j) => j.status === 'queued' || j.status === 'running');
      return active ? 2_000 : false;
    },
  });
}

export function useWorkspaceTokens(workspaceID: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: qk.workspaceTokens(workspaceID ?? ''),
    enabled: Boolean(workspaceID),
    queryFn: async () => {
      const { data, error } = await client.GET('/v1/workspaces/{workspaceID}/api-tokens', {
        params: { path: { workspaceID: workspaceID! } },
      });
      const body = unwrap(data, error);
      return body.items ?? [];
    },
  });
}

interface CreateWorkspaceTokenInput {
  workspaceID: string;
  name: string;
  scope: WorkspaceTokenScope;
  expiresAt?: string;
}

/** Resolves to the issued token; its raw `token` is returned exactly once. */
export function useCreateWorkspaceToken() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceID, ...body }: CreateWorkspaceTokenInput) => {
      const { data, error } = await client.POST('/v1/workspaces/{workspaceID}/api-tokens', {
        params: { path: { workspaceID } },
        body,
      });
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.workspaceTokens(vars.workspaceID) });
    },
  });
}

/** Resolves to the re-issued token; the previous raw token stops working. */
export function useRotateWorkspaceToken() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceID, tokenID }: { workspaceID: string; tokenID: string }) => {
      const { data, error } = await client.POST(
        '/v1/workspaces/{workspaceID}/api-tokens/{tokenID}/rotate',
        { params: { path: { workspaceID, tokenID } } },
      );
      return unwrap(data, error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.workspaceTokens(vars.workspaceID) });
    },
  });
}

export function useDeleteWorkspaceToken() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceID, tokenID }: { workspaceID: string; tokenID: string }) => {
      const { error } = await client.DELETE('/v1/workspaces/{workspaceID}/api-tokens/{tokenID}', {
        params: { path: { workspaceID, tokenID } },
      });
      if (error) throw normaliseError(error);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: qk.workspaceTokens(vars.workspaceID) });
    },
  });
}
